import {CommonModule} from '@angular/common';
import {Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {Subscription} from 'rxjs';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {ExclusiveDropdownService} from '../../services/exclusive-dropdown.service';

@Component({
  selector: 'app-generic-search-select',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './generic-search-select.component.html',
  styleUrl: './generic-search-select.component.css'
})
export class GenericSearchSelectComponent<T> implements OnInit, OnChanges, OnDestroy {
  @Input() label: string = '';
  @Input() placeholder: string = 'Cerca...';
  @Input() id: string = 'search-select-' + Math.random().toString(36).slice(2, 9);
  @Input() control!: FormControl<T | null>;
  @Input() options: T[] = [];
  @Input() minSearchLength: number = 0;
  @Input() maxResults: number = 50;
  @Input() noResultsText: string = 'Nessun risultato trovato';
  @Input() clearOnBlurIfInvalid: boolean = true;
  @Input() displayFn: (item: T) => string = (item: T) => (item as { name?: string })?.name || String(item);
  @Output() itemSelected: EventEmitter<T> = new EventEmitter<T>();
  @Output() deleteItem: EventEmitter<T> = new EventEmitter<T>();

  searchInput: FormControl<string> = new FormControl('', {nonNullable: true});
  filteredOptions: T[] = [];
  showDropdown: boolean = false;
  private sub: Subscription = new Subscription();
  private readonly exclusiveDropdown = inject(ExclusiveDropdownService);
  private hasFocus = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      this.filterOptions(this.searchInput.value);
      this.syncInputFromControl();
    }
  }

  ngOnInit(): void {
    this.syncInputFromControl();

    this.sub.add(
      this.control.valueChanges.subscribe(() => {
        this.syncInputFromControl();
      })
    );

    this.sub.add(
      this.searchInput.valueChanges
        .pipe(debounceTime(50), distinctUntilChanged())
        .subscribe((query) => {
          this.filterOptions(query);
          if (this.hasFocus) {
            this.openDropdown();
          }
        })
    );
  }

  onFocus(): void {
    this.hasFocus = true;
    this.filterOptions(this.searchInput.value);
    this.openDropdown();
  }

  onBlur(): void {
    this.hasFocus = false;
    setTimeout(() => {
      if (this.hasFocus) {
        return;
      }
      this.closeDropdown();

      if (this.control.value) {
        this.searchInput.setValue(this.displayFn(this.control.value), {emitEvent: false});
        return;
      }

      if (this.clearOnBlurIfInvalid) {
        this.searchInput.setValue('', {emitEvent: false});
      }
    }, 180);
  }

  selectItem(item: T): void {
    this.control.setValue(item);
    this.searchInput.setValue(this.displayFn(item), {emitEvent: false});
    this.closeDropdown();
    this.itemSelected.emit(item);
  }

  closeDropdown(): void {
    this.showDropdown = false;
    this.exclusiveDropdown.closed(this);
  }

  clearSelection(): void {
    this.control.setValue(null);
    this.searchInput.setValue('', {emitEvent: false});
    this.filterOptions('');
    this.hasFocus = true;
    this.openDropdown();
  }

  onDeleteItem(event: Event, item: T): void {
    event.stopPropagation();
    this.deleteItem.emit(item);
  }


  private syncInputFromControl(): void {
    const value = this.control?.value;
    const displayValue = value ? this.displayFn(value) : '';
    this.searchInput.setValue(displayValue, {emitEvent: false});
  }

  private filterOptions(rawQuery: string): void {
    const query = this.normalize(rawQuery || '');

    if (query.length < this.minSearchLength) {
      this.filteredOptions = [];
      return;
    }

    this.filteredOptions = this.options
      .filter((item) => this.normalize(this.displayFn(item)).includes(query))
      .slice(0, this.maxResults);
  }

  private normalize(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.closeDropdown();
  }

  private openDropdown(): void {
    this.showDropdown = true;
    this.exclusiveDropdown.open(this);
  }
}

